import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { exportCaCumuleExcel } from "./ca-cumule-excel.utils";

export type CaCumuleTimeScale = "DAY" | "WEEK" | "MONTH";

export interface CaCumulePeriodRow {
  label: string;
  dateKey: string;
  ca: number;
  cumul: number;
}

export interface CaCumuleDialogData {
  tiersDenomination: string;
  rows: CaCumulePeriodRow[];
  scale: CaCumuleTimeScale;
  facturesLignes: any[];
}

interface Stats {
  totalCA: number;
  totalFormatted: string;
  nbPeriodes: number;
  nbPeriodesAvecCA: number;
  avgCA: number;
  avgFormatted: string;
  bestLabel: string;
  bestCA: number;
  bestCAFormatted: string;
  pareto80: number;
}

type SortKey = "date" | "ca";

@Component({
  selector: "vl-ca-cumule-dialog",
  standalone: true,
  imports: [
    CommonModule, SharedModule, DialogHeaderExportComponent,
    DecimalPipe, MatIcon, MatTooltip,
    ChipsToggleComponent,
  ],
  templateUrl: "./ca-cumule-dialog.component.html",
  styleUrl: "./ca-cumule-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaCumuleDialogComponent {
  data = inject<CaCumuleDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<CaCumuleDialogComponent>);

  scale = signal<CaCumuleTimeScale>(this.data.scale);
  scaleElements = [
    { name: "Jour", value: "DAY" as CaCumuleTimeScale },
    { name: "Semaine", value: "WEEK" as CaCumuleTimeScale },
    { name: "Mois", value: "MONTH" as CaCumuleTimeScale },
  ];
  scaleLabelSingularLower: Record<CaCumuleTimeScale, string> = {
    DAY: "jour",
    WEEK: "semaine",
    MONTH: "mois",
  };
  scaleLabelPlural: Record<CaCumuleTimeScale, string> = {
    DAY: "jours",
    WEEK: "semaines",
    MONTH: "mois",
  };
  scaleBestLabel: Record<CaCumuleTimeScale, string> = {
    DAY: "Meilleur jour",
    WEEK: "Meilleure semaine",
    MONTH: "Meilleur mois",
  };

  sortKey = signal<SortKey>("date");
  sortDesc = signal<boolean>(true);

  toggleSort(key: SortKey) {
    if (this.sortKey() === key) {
      this.sortDesc.set(!this.sortDesc());
    } else {
      this.sortKey.set(key);
      this.sortDesc.set(true);
    }
  }

  rawRows = computed<CaCumulePeriodRow[]>(() => {
    const sc = this.scale();
    if (sc === this.data.scale) return this.data.rows;
    return computeRows(this.data.facturesLignes, sc);
  });

  rows = computed<CaCumulePeriodRow[]>(() => {
    const base = [...this.rawRows()];
    const key = this.sortKey();
    const desc = this.sortDesc();
    base.sort((a, b) => {
      if (key === "ca") {
        const d = a.ca - b.ca;
        return desc ? -d : d;
      }
      const d = a.dateKey.localeCompare(b.dateKey);
      return desc ? -d : d;
    });
    return base;
  });

  stats = computed<Stats>(() => {
    const rs = this.rawRows();
    if (!rs.length) {
      return {
        totalCA: 0, totalFormatted: "—",
        nbPeriodes: 0, nbPeriodesAvecCA: 0,
        avgCA: 0, avgFormatted: "—",
        bestLabel: "—", bestCA: 0, bestCAFormatted: "—",
        pareto80: 0,
      };
    }
    const totalCA = rs.reduce((s, r) => s + r.ca, 0);
    const withCA = rs.filter((r) => r.ca > 0);
    const avgCA = withCA.length ? totalCA / withCA.length : 0;
    const best = [...rs].sort((a, b) => b.ca - a.ca)[0];
    const sortedByCa = [...rs].sort((a, b) => b.ca - a.ca);
    let cum = 0;
    let p80 = 0;
    for (const r of sortedByCa) {
      cum += r.ca;
      p80++;
      if (cum >= totalCA * 0.8) break;
    }
    return {
      totalCA,
      totalFormatted: formatEuroShort(totalCA),
      nbPeriodes: rs.length,
      nbPeriodesAvecCA: withCA.length,
      avgCA,
      avgFormatted: formatEuroShort(avgCA),
      bestLabel: best?.label ?? "—",
      bestCA: best?.ca ?? 0,
      bestCAFormatted: formatEuroShort(best?.ca ?? 0),
      pareto80: p80,
    };
  });

  partPct(ca: number): number {
    const total = this.stats().totalCA || 1;
    return (ca / total) * 100;
  }

  formatEuroExact(v: number): string {
    const n = Number(v ?? 0);
    if (!isFinite(n)) return "—";
    return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  exporting = signal(false);

  async exportExcel() {
    this.exporting.set(true);
    try {
      await exportCaCumuleExcel({
        tiersDenomination: this.data.tiersDenomination,
        facturesLignes: this.data.facturesLignes,
      });
    } finally {
      this.exporting.set(false);
    }
  }
}

/* ============================================================================
 * Helpers — format et computation temporelle
 * ============================================================================ */

function formatEuroShort(v: number): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000)
    return `${(n / 1_000_000).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} M€`;
  if (abs >= 10_000)
    return `${(n / 1_000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} k€`;
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}

export function computeRows(
  facturesLignes: any[],
  scale: CaCumuleTimeScale,
): CaCumulePeriodRow[] {
  const byKey = new Map<string, { value: number; d: Date }>();

  for (const fl of facturesLignes) {
    const f = fl?.facture;
    const dRaw = parseDate(f?.date ?? f?.dateString ?? fl?.createdAt);
    if (!dRaw) continue;
    const d = startOf(dRaw, scale);
    const key = makeKey(d, scale);
    const q = parseFloatSafe(fl?.quantite);
    const pu = parseFloatSafe(fl?.prix);
    const amount = q * pu;
    if (!amount) continue;
    const prev = byKey.get(key);
    if (prev) prev.value += amount;
    else byKey.set(key, { value: amount, d });
  }

  if (!byKey.size) return [];

  const vals = [...byKey.values()].sort((a, b) => a.d.getTime() - b.d.getTime());
  const minD = vals[0].d;
  const maxD = vals.at(-1)!.d;

  const timeline: Date[] = [];
  for (let d = new Date(minD); d <= maxD; d = addOne(d, scale)) {
    timeline.push(new Date(d));
  }

  let acc = 0;
  return timeline.map((d) => {
    const key = makeKey(d, scale);
    const ca = byKey.get(key)?.value ?? 0;
    acc += ca;
    return {
      label: formatKey(d, scale),
      dateKey: key,
      ca: Math.round(ca * 100) / 100,
      cumul: Math.round(acc * 100) / 100,
    };
  });
}

function parseDate(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}
function parseFloatSafe(v: any): number {
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return isFinite(n) ? n : 0;
}
function startOf(d: Date, scale: CaCumuleTimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") { dd.setHours(0, 0, 0, 0); return dd; }
  if (scale === "MONTH") return new Date(dd.getFullYear(), dd.getMonth(), 1, 0, 0, 0, 0);
  return startOfISOWeek(dd);
}
function addOne(d: Date, scale: CaCumuleTimeScale): Date {
  const dd = new Date(d);
  if (scale === "DAY") { dd.setDate(dd.getDate() + 1); return startOf(dd, "DAY"); }
  if (scale === "MONTH") return new Date(dd.getFullYear(), dd.getMonth() + 1, 1, 0, 0, 0, 0);
  dd.setDate(dd.getDate() + 7);
  return startOfISOWeek(dd);
}
function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}
function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d.getTime() - firstThursday.getTime()) / 86400000 - 3) / 7);
  return { year: d.getUTCFullYear(), week };
}
function makeKey(d: Date, scale: CaCumuleTimeScale): string {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  if (scale === "DAY") return `${y}-${m}-${day}`;
  if (scale === "MONTH") return `${y}-${m}`;
  const { year, week } = isoWeek(d);
  return `${year}-W${week.toString().padStart(2, "0")}`;
}
function formatKey(d: Date, scale: CaCumuleTimeScale): string {
  if (scale === "DAY")
    return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" }).format(d);
  if (scale === "MONTH")
    return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(d);
  const { year, week } = isoWeek(d);
  return `S${week.toString().padStart(2, "0")} ${year}`;
}
